import { FiHome, FiUsers, FiList, FiBarChart2, FiDatabase, FiMapPin, FiMessageSquare, FiFileText, FiFolder, FiUserCheck, FiSettings, FiPackage, FiHelpCircle, FiCheckSquare, FiInfo, FiLink, FiClipboard } from 'react-icons/fi'
import utils from '../utils/utils'

// Each page entry:
//   path     — appended to /trials/:id/
//   label    — display name
//   detail   — short description shown below label
//   icon     — react-icons component
//   keywords — lowercase strings; a result is returned if the query is a substring of any keyword
//   canView  — optional (user, trial) => bool; omit to allow all trial members
const TRIAL_PAGES = [
  {
    path: 'manage',
    label: 'Overview',
    detail: 'Trial dashboard and summary',
    icon: FiHome,
    keywords: ['overview', 'home', 'dashboard', 'manage', 'summary']
  },
  {
    path: 'participants',
    label: 'Participants',
    detail: 'View and manage trial participants',
    icon: FiUsers,
    keywords: ['participants', 'enrolment', 'enrollment', 'subjects', 'patients']
  },
  {
    path: 'participants/groups',
    label: 'Participant Groups',
    detail: 'Manage and view participant groups',
    icon: FiUsers,
    keywords: ['groups', 'participant groups', 'cohorts', 'arms', 'treatment groups', 'control']
  },
  {
    path: 'stages',
    label: 'Stages',
    detail: 'Design questionnaires and schedule stages',
    icon: FiList,
    keywords: ['stages', 'design', 'questionnaire', 'forms', 'schedule', 'visits', 'crfs', 'crf'],
    canView: (user, trial) => utils.canReadTrial(user, trial)
  },
  {
    path: 'results',
    label: 'Results',
    detail: 'View collected data and responses',
    icon: FiBarChart2,
    keywords: ['results', 'responses', 'outcomes', 'data', 'answers', 'submissions'],
    canView: (user, trial) => utils.canReadTrial(user, trial)
  },
  {
    path: 'data',
    label: 'Data Types',
    detail: 'Configure custom data fields and types',
    icon: FiDatabase,
    keywords: ['data types', 'datatypes', 'data', 'fields', 'variables', 'custom fields'],
    canView: (user, trial) => utils.canReadTrial(user, trial)
  },
  {
    path: 'sites',
    label: 'Sites',
    detail: 'Manage trial sites and locations',
    icon: FiMapPin,
    keywords: ['sites', 'locations', 'centres', 'centers', 'hospitals', 'clinics'],
    canView: (user, trial) => utils.canReadTrialSites(user, trial)
  },
  {
    path: 'messages',
    label: 'Messages',
    detail: 'Participant messaging and communications',
    icon: FiMessageSquare,
    keywords: ['messages', 'messaging', 'chat', 'communication', 'sms', 'email', 'notifications', 'inbox'],
    canView: (user, trial) => utils.canSupportTrial(user, trial)
  },
  {
    path: 'consent',
    label: 'Consent',
    detail: 'Informed consent forms and tracking',
    icon: FiClipboard,
    keywords: ['consent', 'icf', 'informed consent', 'e-consent', 'econsent', 'signature'],
    canView: (user, trial) => utils.canReadTrialConsents(user, trial) || utils.canManageTrial(user, trial)
  },
  {
    path: 'files',
    label: 'Files',
    detail: 'Documents, uploads and approvals',
    icon: FiFolder,
    keywords: ['files', 'documents', 'uploads', 'attachments', 'approvals', 'docs'],
    canView: (user, trial) => utils.canAccessTrial(user, trial)
  },
  {
    path: 'personnel',
    label: 'Personnel',
    detail: 'Team members and access permissions',
    icon: FiUserCheck,
    keywords: ['personnel', 'team', 'staff', 'users', 'access', 'roles', 'permissions', 'investigators', 'members'],
    canView: (user, trial) => utils.canReadTrialUsers(user, trial)
  },
  {
    path: 'settings',
    label: 'Settings',
    detail: 'Trial configuration and options',
    icon: FiSettings,
    keywords: ['settings', 'configuration', 'config', 'options', 'preferences'],
    canView: (user, trial) => utils.canManageTrial(user, trial)
  },
  {
    path: 'settings/integrations',
    label: 'Integrations',
    detail: 'Connect third-party services and devices',
    icon: FiLink,
    keywords: ['integrations', 'connections', 'garmin', 'wearables', 'devices', 'third party', 'api', 'oauth', 'connect'],
    canView: (user, trial) => utils.canManageTrial(user, trial)
  },
  {
    path: 'packs',
    label: 'Packs',
    detail: 'Resource packs and materials for participants',
    icon: FiPackage,
    keywords: ['packs', 'kits', 'resources', 'materials', 'documents'],
    canView: (user, trial) => utils.canListPacksTrial(user, trial)
  },
  {
    path: 'queries',
    label: 'Queries',
    detail: 'Data queries and issue tracking',
    icon: FiHelpCircle,
    keywords: ['queries', 'issues', 'questions', 'discrepancies', 'dcf', 'data clarification'],
    canView: (user, trial) => utils.canReadTrialQueries(user, trial)
  },
  {
    path: 'tasks',
    label: 'Tasks',
    detail: 'Action items and trial tasks',
    icon: FiCheckSquare,
    keywords: ['tasks', 'actions', 'to-do', 'todo', 'checklist']
  },
  {
    path: 'info',
    label: 'Trial Info',
    detail: 'Protocol details and trial information',
    icon: FiInfo,
    keywords: ['info', 'information', 'trial details', 'protocol', 'description', 'about']
  },
  {
    path: 'poll-responses',
    label: 'Poll Responses',
    detail: 'Remote questionnaire response log',
    icon: FiFileText,
    keywords: ['poll', 'polls', 'responses', 'remote', 'public links', 'anonymous'],
    canView: (user, trial) => utils.canReadTrial(user, trial)
  }
]

export function searchPages (trialId, query, user, trial) {
  if (!query || !query.trim() || !trialId) return []
  const q = query.trim().toLowerCase()
  return TRIAL_PAGES.filter(page =>
    (!page.canView || page.canView(user, trial)) &&
    (page.keywords.some(k => k.includes(q)) || page.label.toLowerCase().includes(q))
  ).map(page => ({
    type: 'page',
    _id: page.path,
    label: page.label,
    detail: page.detail,
    icon: page.icon,
    url: `/trials/${trialId}/${page.path}`
  }))
}
